import { __extends, __spreadArray, __rest, __assign } from 'tslib';
import { normalizeRoot, patch, mount, createCommentVNode, callAll, findDomFromVNode, unmount, createComponentVNode, createFragment, createElementVNode, normalizeChildren, createVoidVNode } from 'misstime';
import { isFunction, EMPTY_OBJ, isNull, isUndefined, hasOwn, isEventProp, get, set, throwError, isNullOrUndefined, isObject, isStringOrNumber, isArray, error, hasDocumentAvailable } from 'intact-shared';
import { compile as compile$1, registerCompile } from 'vdt';
export * from 'vdt';

var nextTick = typeof Promise !== 'undefined' ?
    function (callback) { return Promise.resolve().then(function () { return callback(); }); } :
    /* istanbul ignore next */
    function (callback) { return window.setTimeout(callback, 0); };
var microTaskPending = false;
var QUEUE_MAP = new Map();
function renderSyncComponnet(component, lastVNode, nextVNode, parentDom, anchor, mountedQueue) {
    component.$blockRender = true;
    component.trigger('$beforeMount', lastVNode, nextVNode);
    if (isFunction(component.beforeMount)) {
        component.beforeMount(lastVNode, nextVNode);
    }
    component.$blockRender = false;
    var vNode = normalizeRoot(component.$template(EMPTY_OBJ, component.get('$blocks')), nextVNode);
    // reuse the dom even if they are different
    var lastInput = null;
    if (!isNull(lastVNode) && (lastInput = lastVNode.children.$lastInput)) {
        // if (lastVNode !== nextVNode) {
        // we will set nextVNode to lastVNode on render async component, exclude this case
        // unmountComponentClass(lastVNode, nextVNode);
        // }
        patch(lastInput, vNode, parentDom, component, component.$SVG, anchor, mountedQueue, true);
    }
    else {
        mount(vNode, parentDom, component, component.$SVG, anchor, mountedQueue);
    }
    component.$lastInput = vNode;
    mountedQueue.push(function () {
        callAllQueue(component, '$pendingQueue');
        component.$mount(lastVNode, nextVNode);
    });
    component.$rendered = true;
}
function renderAsyncComponent(component, lastVNode, nextVNode, parentDom, anchor, mountedQueue) {
    if (isNull(lastVNode)) {
        // set nextVNode to lastVNode to let renderSyncComponent to patch
        lastVNode = nextVNode;
        var vNode = component.$lastInput = createCommentVNode('async');
        mount(vNode, parentDom, component, component.$SVG, anchor, mountedQueue);
    }
    component.on('$inited', function () {
        mountedQueue = component.$mountedQueue = [];
        renderSyncComponnet(component, lastVNode, nextVNode, parentDom, anchor, mountedQueue);
        // call queue before mountedQueue, because queue are callbacks set in init
        callAllQueue(component, '$pendingQueue');
        callAll(mountedQueue);
    });
}
function updateSyncComponent(component, lastVNode, nextVNode, parentDom, anchor, mountedQueue, force) {
    component.$blockRender = true;
    if (!force) {
        patchProps(component, lastVNode.props, nextVNode.props, component.constructor.defaults());
    }
    component.trigger('$beforeUpdate', lastVNode, nextVNode);
    if (isFunction(component.beforeUpdate)) {
        if (process.env.NODE_ENV !== 'production') {
            DEV_callMethod(component, component.beforeUpdate, lastVNode, nextVNode);
        }
        else {
            /* istanbul ignore next */
            component.beforeUpdate(lastVNode, nextVNode);
        }
    }
    component.$blockRender = false;
    var vNode = normalizeRoot(component.$template(EMPTY_OBJ, component.get('$blocks')), nextVNode);
    patch(component.$lastInput, vNode, parentDom, component, component.$SVG, anchor, mountedQueue, false);
    component.$lastInput = vNode;
    mountedQueue.push(function () {
        component.trigger('$updated', lastVNode, nextVNode);
    });
    if (isFunction(component.updated)) {
        mountedQueue.push(function () {
            if (process.env.NODE_ENV !== 'production') {
                DEV_callMethod(component, component.updated, lastVNode, nextVNode);
            }
            else {
                /* istanbul ignore next */
                component.updated(lastVNode, nextVNode);
            }
        });
    }
}
function updateAsyncComponent(component, lastVNode, nextVNode, parentDom, anchor, mountedQueue, force) {
    component.on('$inited', function () {
        mountedQueue = component.$mountedQueue = [];
        updateSyncComponent(component, lastVNode, nextVNode, parentDom, anchor, mountedQueue, force);
        callAll(mountedQueue);
    });
}
function componentInited(component, triggerReceiveEvents) {
    triggerReceiveEvents && triggerReceiveEvents();
    component.$inited = true;
    component.trigger('$inited');
}
function mountProps(component, nextProps) {
    var props = component.$props;
    var changeTraces = [];
    for (var prop in nextProps) {
        var nextValue = nextProps[prop];
        if (isUndefined(nextValue))
            continue;
        patchProp(component, props, prop, props[prop], nextValue, changeTraces);
    }
    // a callback to trigger $receive events
    return function () { return triggerReceiveEvents(component, changeTraces, true); };
}
function patchProps(component, lastProps, nextProps, defaultProps) {
    lastProps || (lastProps = EMPTY_OBJ);
    nextProps || (nextProps = EMPTY_OBJ);
    if (lastProps !== nextProps) {
        var changeTraces = [];
        var props = component.$props;
        if (nextProps !== EMPTY_OBJ) {
            for (var prop in nextProps) {
                // const lastValue = rollbackToDefault(prop, lastProps[prop as keyof typeof lastProps], defaultProps);
                var lastValue = props[prop]; // use actual props instead of
                var nextValue = rollbackToDefault(prop, nextProps[prop], defaultProps);
                patchProp(component, props, prop, lastValue, nextValue, changeTraces);
            }
        }
        if (lastProps !== EMPTY_OBJ) {
            for (var prop in lastProps) {
                if (!isUndefined(lastProps[prop]) && !hasOwn.call(nextProps, prop)) {
                    patchProp(component, props, prop, props[prop], defaultProps[prop], changeTraces);
                }
            }
        }
        triggerReceiveEvents(component, changeTraces, false);
    }
}
function patchProp(component, props, prop, lastValue, nextValue, changeTraces) {
    if (lastValue === nextValue)
        return;
    props[prop] = nextValue;
    if (!isEventProp(prop)) {
        changeTraces.push({ path: prop, newValue: nextValue, oldValue: lastValue });
    }
}
function setProps(component, newProps) {
    var props = component.$props;
    var changeTracesGroup = [];
    for (var propName in newProps) {
        var lastValue = get(props, propName);
        var nextValue = newProps[propName];
        if (lastValue !== nextValue) {
            changeTracesGroup.push(set(props, propName, nextValue));
        }
    }
    var changesLength = changeTracesGroup.length;
    if (changesLength > 0) {
        // trigger $change events
        for (var i = 0; i < changesLength; i++) {
            var changeTraces = changeTracesGroup[i];
            // we should iterate from back to front to trigger event in order like a.b.c -> a.b -> a
            for (var j = changeTraces.length - 1; j > -1; j--) {
                var _a = changeTraces[j], path = _a.path, newValue = _a.newValue, oldValue = _a.oldValue;
                callModelEvent(component, path, newValue);
                component.trigger("$change:".concat(path), newValue, oldValue);
            }
        }
        component.forceUpdate(function () {
            // trigger $changed events
            for (var i = 0; i < changesLength; i++) {
                var changeTraces = changeTracesGroup[i];
                // we should iterate from back to front to trigger event in order like a.b.c -> a.b -> a
                for (var j = changeTraces.length - 1; j > -1; j--) {
                    var _a = changeTraces[j], path = _a.path, newValue = _a.newValue, oldValue = _a.oldValue;
                    component.trigger("$changed:".concat(path), newValue, oldValue);
                }
            }
        });
    }
}
function callModelEvent(component, path, newValue) {
    var modelEvent = component.$props["ev-$model:".concat(path)];
    if (modelEvent) {
        modelEvent(newValue);
    }
}
function forceUpdate(component, callback) {
    if (!component.$inited) {
        if (isFunction(callback)) {
            (component.$pendingQueue || (component.$pendingQueue = [])).push(callback);
        }
    }
    else if (component.$blockRender) {
        // component is before rendering or updating
        if (isFunction(callback)) {
            // append pendingQueue firstly to make the changed event order correct.
            // @see unit test: set prop multiple times 
            var pendingQueue = component.$pendingQueue;
            var mountedQueue = component.$mountedQueue;
            if (pendingQueue) {
                mountedQueue.push.apply(mountedQueue, pendingQueue);
                component.$pendingQueue = null;
            }
            mountedQueue.push(callback);
        }
    }
    else {
        if (!QUEUE_MAP.has(component)) {
            QUEUE_MAP.set(component, { component: component, next: null });
        }
        // if (QUEUE.indexOf(component) === -1) {
        // QUEUE.push(component);
        // }
        if (!microTaskPending) {
            microTaskPending = true;
            nextTick(rerender);
        }
        if (isFunction(callback)) {
            (component.$queue || (component.$queue = [])).push(callback);
        }
    }
}
function triggerReceiveEvents(component, changeTraces, init) {
    for (var i = 0; i < changeTraces.length; i++) {
        var _a = changeTraces[i], path = _a.path, newValue = _a.newValue, oldValue = _a.oldValue;
        component.trigger("$receive:".concat(path), newValue, oldValue, init);
    }
}
function rerender() {
    microTaskPending = false;
    var trees = getRerenderTrees();
    var tree = undefined;
    while (tree = trees.shift()) {
        var component = tree.component;
        if (!component.$unmounted) {
            var mountedQueue = component.$mountedQueue = [];
            var vNode = component.$vNode;
            component.$update(vNode, vNode, findDomFromVNode(component.$lastInput, true).parentNode, null, mountedQueue, true);
            callAll(mountedQueue);
            callQueueDepthFirst(tree);
        }
    }
}
/**
 * Remove the unnecessary components if we find that
 * their parents is already in the QUEUE_MAP. The parent
 * component will update its children, so we remove
 * all the children components.
 */
function getRerenderTrees() {
    var trees = [];
    QUEUE_MAP.forEach(function (tree, component) {
        var senior = component;
        while (senior = senior.$senior) {
            var seniorTree = QUEUE_MAP.get(senior);
            if (!isUndefined(seniorTree)) {
                seniorTree.next = tree;
                return;
            }
        }
        trees.push(tree);
    });
    QUEUE_MAP.clear();
    return trees;
}
function callQueueDepthFirst(tree) {
    var loop = function (tree) {
        if (!tree)
            return;
        loop(tree.next);
        callAllQueue(tree.component, '$queue');
    };
    loop(tree);
}
function callAllQueue(component, queueName) {
    var queue = component[queueName];
    if (queue) {
        for (var i = 0; i < queue.length; i++) {
            queue[i].call(component);
        }
        component[queueName] = null;
    }
}
function rollbackToDefault(prop, value, defaultProps) {
    return isUndefined(value) ? defaultProps[prop] : value;
}
function DEV_callMethod(component, method, lastVNode, nextVNode) {
    component.$blockAddEvent = true;
    method.call(component, lastVNode, nextVNode);
    component.$blockAddEvent = false;
}

var Event = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function () {
    function Event() {
        this.$events = {};
        // internal properties
        this.$blockAddEvent = false;
    }
    Event.prototype.on = function (name, callback) {
        if (process.env.NODE_ENV !== 'production') {
            if (this.$blockAddEvent) {
                throwError('Adding event listener on `beforeUpdate` & `updated` is not allowed, ' +
                    'because it may invoke multiple times. ' +
                    'You should add it on `init`.');
            }
            if (!isFunction(callback)) {
                throwError('Expect a function, but got ' + JSON.stringify(callback));
            }
        }
        var events = this.$events;
        (events[name] || (events[name] = [])).push(callback);
    };
    Event.prototype.off = function (name, callback) {
        if (isUndefined(name)) {
            this.$events = {};
            return;
        }
        var callbacks = this.$events[name];
        if (isUndefined(callbacks))
            return;
        if (isUndefined(callback)) {
            this.$events[name] = undefined;
            return;
        }
        for (var i = 0; i < callbacks.length; i++) {
            if (callbacks[i] === callback) {
                callbacks.splice(i, 1);
                break;
            }
        }
    };
    // trigger<N = void, T extends (...args: any[]) => void = any>(name: NoInfer<N>, ...args: Parameters<T>): void;
    Event.prototype.trigger = function (name) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var callbacks = this.$events[name];
        if (!isUndefined(callbacks)) {
            callbacks = callbacks.slice();
            for (var i = 0; i < callbacks.length; i++) {
                callbacks[i].apply(void 0, args);
            }
        }
    };
    return Event;
}());

function watch(key, callback, options, instance) {
    if (instance === void 0) { instance = currentInstance; }
    if (process.env.NODE_ENV !== 'production') {
        if (!instance) {
            throwError('watch() can only be used inside init()');
        }
    }
    if (!options || !options.presented) {
        // instance!.on(`$change:${key}` as `$change:${string & K}`, (newValue, oldValue) => {
        // const realNewValue = instance!.get(key);
        // if (realNewValue !== newValue) {
        // oldValue = newValue
        // newValue = realNewValue;
        // }
        // callback(newValue, oldValue);
        // });
        // @ts-ignore
        instance.on("$change:".concat(key), function (newValue, oldValue) {
            /**
             * maybe the newValue has been changed, see intact-react unit test: update in updating
             */
            newValue = instance.get(key);
            callback(newValue, oldValue);
        });
        if (!options || !options.inited) {
            // @ts-ignore
            instance.on("$receive:".concat(key), callback);
        }
        else {
            // @ts-ignore
            instance.on("$receive:".concat(key), function (newValue, oldValue, init) {
                if (!init) {
                    callback(newValue, oldValue);
                }
            });
        }
    }
    else {
        // @ts-ignore
        instance.on("$changed:".concat(key), function (newValue, oldValue) {
            newValue = instance.get(key);
            callback(newValue, oldValue);
        });
        if (!options.inited) {
            // @ts-ignore
            instance.on("$receive:".concat(key), function (newValue, oldValue, init) {
                var lifecycle = init ? '$mounted' : '$updated';
                var fn = function () {
                    instance.off(lifecycle, fn);
                    callback(newValue, oldValue);
                };
                instance.on(lifecycle, fn);
            });
        }
        else {
            // @ts-ignore
            instance.on("$receive:".concat(key), function (newValue, oldValue, init) {
                if (!init) {
                    var fn_1 = function () {
                        instance.off('$updated', fn_1);
                        callback(newValue, oldValue);
                    };
                    instance.on('$updated', fn_1);
                }
            });
        }
    }
}

var currentInstance = null;
function useInstance() {
    return currentInstance;
}
function setInstance(instance) {
    currentInstance = instance;
}
// type HasDefault<P, U = Partial<P>> = {
// [K in keyof P]: K extends keyof U ?
// Exclude<P[K], undefined> :
// P[K]
// };
var uid = 0;
var Component = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function (_super) {
    __extends(Component, _super);
    function Component(props, $vNode, $SVG, $mountedQueue, $senior) {
        var _this = _super.call(this) || this;
        _this.refs = {};
        _this.$SVG = false;
        // internal properties
        _this.$lastInput = null;
        _this.$blockRender = false;
        _this.$queue = null;
        _this.$pendingQueue = null;
        _this.$provides = null;
        _this.$uid = uid++;
        // lifecyle states
        _this.$inited = false;
        _this.$rendered = false;
        _this.$mounted = false;
        _this.$unmounted = false;
        // should trigger recieve events on initializing or not
        _this.triggerReceiveEvents = null;
        currentInstance = _this;
        _this.$vNode = $vNode;
        _this.$SVG = $SVG;
        _this.$mountedQueue = $mountedQueue;
        _this.$senior = $senior;
        var constructor = _this.constructor;
        var template = constructor.template;
        if (isFunction(template)) {
            _this.$template = template;
        }
        else {
            _this.$template = compile$1(template);
        }
        _this.$props = constructor.defaults();
        if (!isNullOrUndefined($senior)) {
            _this.$provides = $senior.$provides;
        }
        if (!isNullOrUndefined(props)) {
            // should mount props in contructor, because we may get props on hooks
            // that initialize in constructor.
            _this.triggerReceiveEvents = mountProps(_this, props);
        }
        return _this;
        // if (isFunction(this.init)) {
        // const ret = this.init(props);
        // if (ret && ret.then) {
        // (ret as Promise<any>).then(() => componentInited(this, triggerReceiveEvents), err => {
        // if (process.env.NODE_ENV !== 'production') {
        // console.error('Unhandled promise rejection in init: ', err);
        // }
        // componentInited(this, triggerReceiveEvents);
        // });
        // } else {
        // componentInited(this, triggerReceiveEvents);
        // }
        // } else {
        // componentInited(this, triggerReceiveEvents);
        // }
    }
    Component.prototype.$init = function (props) {
        var _this = this;
        // if (isFunction(this.init)) {
        var triggerReceiveEvents = this.triggerReceiveEvents;
        if (isFunction(this.init)) {
            var ret = this.init(props);
            if (ret && ret.then) {
                ret.then(function () { return componentInited(_this, triggerReceiveEvents); }, function (err) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('Unhandled promise rejection in init: ', err);
                    }
                    componentInited(_this, triggerReceiveEvents);
                });
            }
            else {
                componentInited(this, triggerReceiveEvents);
            }
        }
        else {
            componentInited(this, triggerReceiveEvents);
        }
        // } else {
        // if it does not exist init method, it is unnecessary to trigger $receive events
        // @MODIFY: maybe init in constructor
        // if (!isNull(props)) {
        // const defaults = this.props;
        // for (const key in props) {
        // const value = props[key];
        // if (!isUndefined(value)) {
        // defaults[key] = value;
        // }
        // }
        // }
        // componentInited(this, null);
        // }
        currentInstance = null;
    };
    Component.prototype.$render = function (lastVNode, nextVNode, parentDom, anchor, mountedQueue) {
        if (this.$inited) {
            renderSyncComponnet(this, lastVNode, nextVNode, parentDom, anchor, mountedQueue);
        }
        else {
            renderAsyncComponent(this, lastVNode, nextVNode, parentDom, anchor, mountedQueue);
        }
    };
    Component.prototype.$mount = function (lastVNode, nextVNode) {
        this.$mounted = true;
        this.trigger('$mounted', lastVNode, nextVNode);
        if (isFunction(this.mounted)) {
            this.mounted(lastVNode, nextVNode);
        }
    };
    Component.prototype.$update = function (lastVNode, nextVNode, parentDom, anchor, mountedQueue, force) {
        if (this.$inited) {
            updateSyncComponent(this, lastVNode, nextVNode, parentDom, anchor, mountedQueue, force);
        }
        else {
            updateAsyncComponent(this, lastVNode, nextVNode, parentDom, anchor, mountedQueue, force);
        }
    };
    Component.prototype.$unmount = function (vNode, nextVNode) {
        /* istanbul ignore next */
        if (process.env.NODE_ENV !== 'production') {
            if (this.$unmounted) {
                throwError('You are unmounting a component that has already been unmounted. ' +
                    'Maybe this is a bug of Intact or it has crashed, open an issue please.');
            }
        }
        this.trigger('$beforeUnmount', vNode, nextVNode);
        if (isFunction(this.beforeUnmount)) {
            this.beforeUnmount(vNode, nextVNode);
        }
        // if (isNull(nextVNode)) {
        // if nextVNode exists, we will unmount it on $render
        unmount(this.$lastInput, null);
        // }
        this.$unmounted = true;
        this.trigger('$unmounted', vNode, nextVNode);
        this.off();
        if (isFunction(this.unmounted)) {
            this.unmounted(vNode, nextVNode);
        }
    };
    Component.prototype.set = function (key, value, options) {
        var _a;
        if (isObject(key)) {
            options = value;
        }
        else {
            key = (_a = {}, _a[key] = value, _a);
        }
        if (!isUndefined(options) && options.silent) {
            for (var propName in key) {
                set(this.$props, propName, key[propName]);
            }
            return;
        }
        setProps(this, key);
    };
    Component.prototype.get = function (key) {
        if (isUndefined(key))
            return this.$props;
        return get(this.$props, key);
    };
    Component.prototype.forceUpdate = function (callback) {
        if (this.$unmounted)
            return;
        forceUpdate(this, callback);
    };
    // trigger<N = void, T extends (...args: any[]) => void = any>(name: NoInfer<N>, ...args: Parameters<T>): void;
    // trigger<K extends keyof E>(name: K, ...args: Parameters<EventCallback>): void;
    // trigger<K extends keyof E>(name: K, ...args: E[K] extends (...args: infer P) => any ? P : never) {
    Component.prototype.trigger = function (name) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        // call event on props firstly
        var propEvent = this.$props["ev-".concat(name)];
        if (isFunction(propEvent) && !this.$unmounted) {
            propEvent.apply(void 0, args);
        }
        _super.prototype.trigger.apply(this, __spreadArray([name], args, false));
    };
    Component.prototype.watch = function (key, callback, options) {
        watch(key, callback, options, this);
    };
    Component.defaults = function () { return ({}); };
    return Component;
}(Event));

var BaseTransition = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function (_super) {
    __extends(BaseTransition, _super);
    function BaseTransition() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        // private isLeaving: boolean = false;
        _this.leavingVNodesCache = {};
        _this.el = null;
        _this.originalDisplay = '';
        return _this;
    }
    BaseTransition.template = function () {
        var props = this.$props;
        var children = props.children;
        if (isNullOrUndefined(children))
            return null;
        if (process.env.NODE_ENV !== 'production') {
            if (isArray(children) && children.length > 1) {
                error("<Transition> can only be used on a single element or component. " +
                    "Use <TransitionGroup> for lists.");
            }
            else if (isInvalidTranstionChild(children)) {
                throwError("<Transtion> received a invalid element: ".concat(JSON.stringify(children), "."));
            }
        }
        // const {mode} = props;
        var vNode = (isArray(children) ? children[0] : children);
        // if (this.isLeaving) {
        // return null;
        // }
        var enterHooks = resolveTransitionHooks(vNode, props, this, false);
        setTransitionHooks(vNode, enterHooks);
        return vNode;
    };
    BaseTransition.defaults = function () {
        return { show: true };
    };
    BaseTransition.prototype.mounted = function () {
        var lastInput = this.$lastInput;
        // is a void vNode 
        if (lastInput.type & 1 /* Text */)
            return;
        var el = this.el = findDomFromVNode(lastInput, true);
        var display = el.style.display;
        this.originalDisplay = display === 'none' ? '' : display;
        if (!this.$props.show) {
            setDisplay(el, 'none');
        }
    };
    BaseTransition.prototype.updated = function (lastVNode, nextVNode) {
        var lastInput = this.$lastInput;
        var transition = lastInput.transition;
        // maybe the element has been removed
        if (!transition)
            return;
        var lastValue = (lastVNode.props || EMPTY_OBJ).show;
        var nextValue = (nextVNode.props || EMPTY_OBJ).show;
        var oldEl = this.el;
        // maybe the element has changed
        var el = this.el = findDomFromVNode(lastInput, true);
        var hasChagned = el !== oldEl;
        if ((lastValue !== nextValue || hasChagned) && el.nodeType === 1) {
            if (nextValue || isUndefined(nextValue)) {
                transition.beforeEnter(el);
                setDisplay(el, this.originalDisplay);
                transition.enter(el);
            }
            else {
                if (hasChagned) {
                    setDisplay(el, 'none');
                }
                else {
                    transition.leave(el, function () {
                        setDisplay(el, 'none');
                    });
                }
            }
        }
    };
    BaseTransition.prototype.unmounted = function () {
        var el = this.el;
        if (el) {
            el.$TD = undefined;
        }
    };
    return BaseTransition;
}(Component));
function resolveTransitionHooks(vNode, props, component, isTransitionGroup) {
    var appear = props.appear, 
    // mode,
    onBeforeEnter = props.onBeforeEnter, onEnter = props.onEnter, onAfterEnter = props.onAfterEnter, onEnterCancelled = props.onEnterCancelled, onBeforeLeave = props.onBeforeLeave, onLeave = props.onLeave, onAfterLeave = props.onAfterLeave, onLeaveCancelled = props.onLeaveCancelled, onBeforeAppear = props.onBeforeAppear, onAppear = props.onAppear, onAfterAppear = props.onAfterAppear, onAppearCancelled = props.onAppearCancelled;
    var key = vNode.key;
    var leavingVNodesCache = component.leavingVNodesCache;
    return {
        beforeEnter: function (el) {
            var hook = onBeforeEnter;
            if (!component.$mounted) {
                if (appear) {
                    hook = onBeforeAppear || onBeforeEnter;
                }
                else {
                    return;
                }
            }
            // for same element (show)
            if (el._leaveCb) {
                el._leaveCb(true);
            }
            // for toggled element with the same key (v-if)
            var leavingVNode = leavingVNodesCache[key];
            if (leavingVNode && vNode.tag === leavingVNode.tag) {
                var el_1 = findDomFromVNode(leavingVNode, true);
                var leaveCb = el_1._leaveCb;
                if (leaveCb) {
                    // Maybe we are patching Fragment and treat the leaving element
                    // as anchor. If we toggle the same key element, the element
                    // will be removed, then the anchor is missing. So we do removing
                    // after patching has ended.
                    component.$mountedQueue.push(leaveCb);
                    // leaveCb();
                }
            }
            hook && hook(el);
        },
        enter: function (el) {
            // maybe a mount enter is processing, and we enter it again by updating its `show` value 
            if (el._enterCb) {
                // el._enterCb(true);
                return;
            }
            var hook = onEnter;
            var afterHook = onAfterEnter;
            var cancelHook = onEnterCancelled;
            if (!component.$mounted) {
                if (!appear)
                    return;
                hook = onAppear || onEnter;
                afterHook = onAfterAppear || onAfterEnter;
                cancelHook = onAppearCancelled || onEnterCancelled;
            }
            var called = false;
            var done = el._enterCb = function (cancelled) {
                if (called)
                    return;
                called = true;
                if (cancelled) {
                    cancelHook && cancelHook(el);
                }
                else {
                    afterHook && afterHook(el);
                }
                el._enterCb = undefined;
            };
            if (hook) {
                hook(el, done);
                if (hook.length <= 1) {
                    done();
                }
            }
            else {
                done();
            }
        },
        leave: function (el, remove) {
            if (el._enterCb) {
                el._enterCb(true);
            }
            if (component.$unmounted) {
                return remove();
            }
            onBeforeLeave && onBeforeLeave(el);
            var called = false;
            var done = el._leaveCb = function (cancelled) {
                if (called)
                    return;
                called = true;
                remove();
                if (cancelled) {
                    onLeaveCancelled && onLeaveCancelled(el);
                }
                else {
                    onAfterLeave && onAfterLeave(el);
                }
                el._leaveCb = undefined;
                if (leavingVNodesCache[key] === vNode) {
                    delete leavingVNodesCache[key];
                }
            };
            leavingVNodesCache[key] = vNode;
            if (onLeave) {
                onLeave(el, done);
                if (onLeave.length <= 1) {
                    done();
                }
            }
            else {
                done();
            }
        },
        show: isTransitionGroup ? true : props.show
    };
}
function setTransitionHooks(vNode, hooks) {
    if (vNode.type & 8 /* ComponentClass */) {
        var component = vNode.children;
        if (!isNullOrUndefined(component) && component.$inited) {
            setTransitionHooks(component.$lastInput, hooks);
        }
        else {
            vNode.transition = hooks;
        }
    }
    else {
        vNode.transition = hooks;
    }
}
function setDisplay(dom, display) {
    dom.$TD = dom.style.display = display;
}
function isInvalidTranstionChild(vNode) {
    return isStringOrNumber(vNode) ||
        typeof vNode === 'boolean' ||
        vNode.type & 2081 /* TextElement */;
}

var transitionProp = 'transition';
var animationProp = 'animation';
var endEvents = [];
var nodeOps = /*#__PURE__*/ (function () {
    var addClass;
    var removeClass;
    var raf;
    var caf;
    if (hasDocumentAvailable) {
        raf = window.requestAnimationFrame ||
            window.webkitRequestAnimationFrame ||
            window.mozRequestAnimationFrame ||
            window.msRequestAnimationFrame ||
            window.setTimeout;
        caf = window.cancelAnimationFrame ||
            window.webkitCancelAnimationFrame ||
            window.mozCancelRequestAnimationFrame ||
            window.msAnimationFrame ||
            window.clearTimeout;
        var testEl = document.createElement('div');
        // set handle class function
        if (testEl.classList) {
            addClass = function (dom, className) { return dom.classList.add(className); };
            removeClass = function (dom, className) { return dom.classList.remove(className); };
        }
        else {
            var hasClass_1 = function (dom, className) { return " ".concat(dom.className, " ").indexOf(" ".concat(className, " ")) > -1; };
            addClass = function (dom, className) { return !hasClass_1(dom, className) && (dom.className += ' ' + className); };
            removeClass = function (dom, className) {
                if (hasClass_1(dom, className)) {
                    dom.className = dom.className
                        .replace(new RegExp("(^|\\s)".concat(className, "(?:\\s|$)"), 'g'), '$1')
                        .replace(/\s+/g, ' ') // multiple spaces to one
                        .replace(/^\s*|\s*$/g, ''); // trim the ends
                }
            };
        }
        var EVENT_NAME_MAP = {
            transitionend: {
                'transition': 'transitionend',
                'WebkitTransition': 'webkitTransitionEnd',
                'MozTransition': 'mozTransitionEnd',
                'OTransition': 'oTransitionEnd',
                'msTransition': 'MSTransitionEnd'
            },
            animationend: {
                'animation': 'animationend',
                'WebkitAnimation': 'webkitAnimationEnd',
                'MozAnimation': 'mozAnimationEnd',
                'OAnimation': 'oAnimationEnd',
                'msAnimation': 'MSAnimationEnd'
            }
        };
        var style = testEl.style;
        // On some platforms, in particular some releases of Android 4.x,
        // the un-prefixed "animation" and "transition" properties are defined on the
        // style object but the events that fire will still be prefixed, so we need
        // to check if the un-prefixed events are useable, and if not remove them
        // from the map
        if (!('AnimationEvent' in window)) {
            delete EVENT_NAME_MAP.animationend.animation;
        }
        if (!('TransitionEvent' in window)) {
            delete EVENT_NAME_MAP.transitionend.transition;
        }
        for (var baseEventName in EVENT_NAME_MAP) {
            var baseEvents = EVENT_NAME_MAP[baseEventName];
            for (var styleName in baseEvents) {
                if (styleName in style) {
                    endEvents.push(baseEvents[styleName]);
                    if (baseEventName === 'transitionend') {
                        transitionProp = styleName;
                    }
                    else {
                        animationProp = styleName;
                    }
                    break;
                }
            }
        }
    }
    return { raf: raf, caf: caf, addClass: addClass, removeClass: removeClass };
})();
var TransitionEvents = {
    on: function (dom, eventListener) {
        if (endEvents.length === 0) {
            // If CSS transitions are not supported, trigger an "end animation"
            // event immediately.
            window.setTimeout(eventListener, 0);
            return;
        }
        endEvents.forEach(function (endEvent) {
            dom.addEventListener(endEvent, eventListener, false);
        });
    },
    off: function (dom, eventListener) {
        if (endEvents.length === 0) {
            return;
        }
        endEvents.forEach(function (endEvent) {
            dom.removeEventListener(endEvent, eventListener, false);
        });
    },
    // one: function(node, eventListener) {
    // let listener = function() {
    // eventListener.apply(this, arguments);
    // TransitionEvents.off(node, listener);
    // };
    // TransitionEvents.on(node, listener);
    // }
};
function nextFrame(fn) {
    var innerFrame;
    var raf = nodeOps.raf, caf = nodeOps.caf;
    var frame = raf(function () {
        innerFrame = raf(fn);
    });
    return function () {
        caf(frame);
        caf(innerFrame);
    };
}
// @reference Vue
var endId = 0;
function whenTransitionEnds(dom, cb) {
    var id = dom.$ID = ++endId;
    var _a = getTransitionInfo(dom), timeout = _a.timeout, propCount = _a.propCount;
    var ended = 0;
    var onEnd = function (e) {
        if (e.target === dom && ++ended >= propCount) {
            end();
        }
    };
    var end = function () {
        TransitionEvents.off(dom, onEnd);
        clearTimeout(timer);
        if (id === dom.$ID) {
            cb();
        }
    };
    var timer = setTimeout(function () {
        if (ended < propCount) {
            end();
        }
    }, timeout + 1);
    TransitionEvents.on(dom, onEnd);
}
function getTransitionInfo(dom) {
    var styles = window.getComputedStyle(dom);
    var getStyleProperties = function (key) { return (styles[key] || '').split(', '); };
    var transitionDelays = getStyleProperties(transitionProp + 'Delay');
    var transitionDurations = getStyleProperties(transitionProp + 'Duration');
    var transitionTimeout = getTimeout(transitionDelays, transitionDurations);
    var animationDelays = getStyleProperties(animationProp + 'Delay');
    var animationDurations = getStyleProperties(animationProp + 'Duration');
    var animationTimeout = getTimeout(animationDelays, animationDurations);
    var type = transitionTimeout > animationTimeout ? 0 /* Transition */ : 1 /* Animation */;
    var timeout = Math.max(transitionTimeout, animationTimeout);
    var propCount = type === 0 /* Transition */ ? transitionDurations.length : animationDurations.length;
    return { type: type, timeout: timeout, propCount: propCount };
}
function getTimeout(delays, durations) {
    var l = delays.length;
    return Math.max.apply(null, durations.map(function (d, i) { return toMs(d) + toMs(delays[i % l]); }));
}
function toMs(s) {
    return s.slice(0, -1) * 1000;
}

var Transition = function (props) {
    return createComponentVNode(8 /* ComponentClass */, BaseTransition, resolveTransitionProps(props), props.key, props.ref);
};
var TransitionTypeDefs = {
    name: String,
    // type: String,
    css: Boolean,
    // duration: [String, Number, Object],
    enterFromClass: String,
    enterActiveClass: String,
    enterToClass: String,
    appearFromClass: String,
    appearActiveClass: String,
    appearToClass: String,
    leaveFromClass: String,
    leaveActiveClass: String,
    leaveToClass: String,
};
Transition.typeDefs = TransitionTypeDefs;
function resolveTransitionProps(props) {
    var _a = props.name, name = _a === void 0 ? 'transition' : _a, 
    // type,
    _b = props.css, 
    // type,
    css = _b === void 0 ? true : _b, 
    // duration,
    _c = props.enterFromClass, 
    // duration,
    enterFromClass = _c === void 0 ? "".concat(name, "-enter-from") : _c, _d = props.enterActiveClass, enterActiveClass = _d === void 0 ? "".concat(name, "-enter-active") : _d, _e = props.enterToClass, enterToClass = _e === void 0 ? "".concat(name, "-enter-to") : _e, _f = props.appearFromClass, appearFromClass = _f === void 0 ? enterFromClass : _f, _g = props.appearActiveClass, appearActiveClass = _g === void 0 ? enterActiveClass : _g, _h = props.appearToClass, appearToClass = _h === void 0 ? enterToClass : _h, _j = props.leaveFromClass, leaveFromClass = _j === void 0 ? "".concat(name, "-leave-from") : _j, _k = props.leaveActiveClass, leaveActiveClass = _k === void 0 ? "".concat(name, "-leave-active") : _k, _l = props.leaveToClass, leaveToClass = _l === void 0 ? "".concat(name, "-leave-to") : _l, baseProps = __rest(props, ["name", "css", "enterFromClass", "enterActiveClass", "enterToClass", "appearFromClass", "appearActiveClass", "appearToClass", "leaveFromClass", "leaveActiveClass", "leaveToClass"]);
    if (!css)
        return baseProps;
    // let enterDuration: number | null = null;
    // let leaveDuration: number | null = null;
    // if (isObject(duration)) {
    // enterDuration = duration.enter;
    // leaveDuration = duration.leave;
    // } else if (!isNullOrUndefined(duration)) {
    // enterDuration = leaveDuration = duration;
    // }
    var onBeforeEnter = baseProps.onBeforeEnter, onEnter = baseProps.onEnter, onEnterCancelled = baseProps.onEnterCancelled, onLeave = baseProps.onLeave, onLeaveCancelled = baseProps.onLeaveCancelled, _m = baseProps.onBeforeAppear, onBeforeAppear = _m === void 0 ? onBeforeEnter : _m, _o = baseProps.onAppear, onAppear = _o === void 0 ? onEnter : _o, _p = baseProps.onAppearCancelled, onAppearCancelled = _p === void 0 ? onEnterCancelled : _p;
    // let cancelNextFrame: (() => void) | null = null;
    var cancelFrame = function (el) {
        var cancelNextFrame = el._cancelNextFrame;
        if (cancelNextFrame) {
            cancelNextFrame();
            forceReflow();
            el._cancelNextFrame = null;
        }
    };
    var finishEnter = function (el, isAppear, done) {
        if (isAppear) {
            if (el._cancelNextFrame) {
                removeTransitionClass(el, appearFromClass);
            }
            else {
                removeTransitionClass(el, appearToClass);
            }
            removeTransitionClass(el, appearActiveClass);
        }
        else {
            if (el._cancelNextFrame) {
                removeTransitionClass(el, enterFromClass);
            }
            else {
                removeTransitionClass(el, enterToClass);
            }
            removeTransitionClass(el, enterActiveClass);
        }
        done && done();
    };
    var finishLeave = function (el, done) {
        if (el._cancelNextFrame) {
            removeTransitionClass(el, leaveFromClass);
        }
        else {
            removeTransitionClass(el, leaveToClass);
        }
        removeTransitionClass(el, leaveActiveClass);
        done && done();
    };
    var makeEnterHook = function (isAppear) {
        return function (el, done) {
            var hook = isAppear ? onAppear : onEnter;
            var resolve = function () { return finishEnter(el, isAppear, done); };
            hook && hook(el, resolve);
            el._cancelNextFrame = nextFrame(function () {
                if (isAppear) {
                    removeTransitionClass(el, appearFromClass);
                    addTransitionClass(el, appearToClass);
                }
                else {
                    removeTransitionClass(el, enterFromClass);
                    addTransitionClass(el, enterToClass);
                }
                if (isUndefined(hook) || hook.length <= 1) {
                    whenTransitionEnds(el, resolve);
                }
                el._cancelNextFrame = null;
            });
        };
    };
    return __assign(__assign({}, baseProps), { onBeforeEnter: function (el) {
            onBeforeEnter && onBeforeEnter(el);
            addTransitionClass(el, enterFromClass);
            addTransitionClass(el, enterActiveClass);
        }, onEnter: makeEnterHook(false), onEnterCancelled: function (el) {
            finishEnter(el, false);
            cancelFrame(el);
            onEnterCancelled && onEnterCancelled(el);
        }, onBeforeAppear: function (el) {
            onBeforeAppear && onBeforeAppear(el);
            addTransitionClass(el, appearFromClass);
            addTransitionClass(el, appearActiveClass);
        }, onAppear: makeEnterHook(true), onAppearCancelled: function (el) {
            finishEnter(el, true);
            cancelFrame(el);
            onAppearCancelled && onAppearCancelled(el);
        }, onLeave: function (el, done) {
            var resolve = function () { return finishLeave(el, done); };
            addTransitionClass(el, leaveFromClass);
            forceReflow();
            addTransitionClass(el, leaveActiveClass);
            el._cancelNextFrame = nextFrame(function () {
                removeTransitionClass(el, leaveFromClass);
                addTransitionClass(el, leaveToClass);
                if (isUndefined(onLeave) || onLeave.length <= 1) {
                    whenTransitionEnds(el, resolve);
                }
                el._cancelNextFrame = null;
            });
            onLeave && onLeave(el, resolve);
        }, onLeaveCancelled: function (el) {
            finishLeave(el);
            cancelFrame(el);
            onLeaveCancelled && onLeaveCancelled(el);
        } });
}
function addTransitionClass(el, className) {
    nodeOps.addClass(el, className);
    (el.$TC || (el.$TC = {}))[className] = true;
}
function removeTransitionClass(el, className) {
    nodeOps.removeClass(el, className);
    var transitionClassname = el.$TC;
    if (transitionClassname) {
        delete transitionClassname[className];
        if (Object.keys(transitionClassname).length === 0) {
            el.$TC = undefined;
        }
    }
}
function forceReflow() {
    return document.body.offsetHeight;
}

var TransitionGroup = /*#__PURE__*/ /*#__PURE__*/ /** @class */ (function (_super) {
    __extends(TransitionGroup, _super);
    function TransitionGroup() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.lastChildren = [];
        _this.children = [];
        return _this;
    }
    TransitionGroup.template = function () {
        var props = this.$props;
        var tag = props.tag, children = props.children;
        var vNode;
        if (isUndefined(tag)) {
            vNode = createFragment(children, 8 /* HasKeyedChildren */);
        }
        else {
            vNode = createElementVNode(2 /* CommonElement */, tag, children, 8 /* HasKeyedChildren */);
        }
        normalizeChildren(vNode, children);
        var lastChildren = this.lastChildren = this.children;
        var nextChildren = vNode.children;
        // convert to array if it has only one child
        var childrenType = vNode.childrenType;
        if (childrenType & 2 /* HasVNodeChildren */) {
            vNode.childrenType = 8 /* HasKeyedChildren */;
            nextChildren = vNode.children = [nextChildren];
        }
        else if (childrenType & 1 /* HasInvalidChildren */) {
            vNode.childrenType = 2 /* HasVNodeChildren */;
            vNode.children = createVoidVNode();
            nextChildren = [];
        }
        if (process.env.NODE_ENV !== 'production') {
            if (isInvalidTranstionChild(nextChildren)) {
                throwError("<Transtion> received a invalid element: ".concat(JSON.stringify(children), "."));
            }
        }
        this.children = nextChildren;
        var cssTransitionProps = resolveTransitionProps(props);
        for (var i = 0; i < nextChildren.length; i++) {
            var child = nextChildren[i];
            if (process.env.NODE_ENV !== 'production') {
                if (child.type & 32768 /* PrefixedKey */) {
                    error('The children of <TransitionGroup> must have key');
                }
            }
            setTransitionHooks(child, resolveTransitionHooks(child, cssTransitionProps, this, true));
        }
        for (var i = 0; i < lastChildren.length; i++) {
            var child = lastChildren[i];
            setTransitionHooks(child, resolveTransitionHooks(child, cssTransitionProps, this, true));
            var dom = findDomFromVNode(child, true);
            child.position = dom.getBoundingClientRect();
        }
        return vNode;
    };
    TransitionGroup.defaults = function () {
        return { move: true };
    };
    TransitionGroup.prototype.mounted = function () { };
    TransitionGroup.prototype.updated = function () {
        var lastChildren = this.lastChildren;
        if (!lastChildren.length) {
            return;
        }
        var props = this.$props;
        if (!props.move)
            return;
        var moveClass = props.moveClass || "".concat(props.name || 'transition', "-move");
        var lastChildrenLength = lastChildren.length;
        for (var i = 0; i < lastChildrenLength; i++) {
            callPendingCbs(lastChildren[i]);
        }
        for (var i = 0; i < lastChildrenLength; i++) {
            var vNode = lastChildren[i];
            vNode.newPosition = findDomFromVNode(vNode, true).getBoundingClientRect();
        }
        var movedChildren = [];
        for (var i = 0; i < lastChildrenLength; i++) {
            var vNode = lastChildren[i];
            if (applyTransition(vNode)) {
                movedChildren.push(vNode);
            }
        }
        forceReflow();
        var _loop_1 = function (i) {
            var vNode = movedChildren[i];
            var el = findDomFromVNode(vNode, true);
            var style = el.style;
            addTransitionClass(el, moveClass);
            style.transform = style.webkitTransform = style.transitionDuration = style.webkitTransitionDuration = '';
            var cb = el._moveCb = function (e) {
                if (e && e.target !== el)
                    return;
                if (!e || transformRegExp.test(e.propertyName)) {
                    TransitionEvents.off(el, cb);
                    el._moveCb = undefined;
                    removeTransitionClass(el, moveClass);
                }
            };
            // TransitionEvents.on(el, cb as EventListener);
            whenTransitionEnds(el, cb);
        };
        for (var i = 0; i < movedChildren.length; i++) {
            _loop_1(i);
        }
    };
    return TransitionGroup;
}(BaseTransition));
var transformRegExp = /transform$/;
function callPendingCbs(vNode) {
    var el = findDomFromVNode(vNode, true);
    if (el._moveCb) {
        el._moveCb();
    }
    // if we call enterCb, it will call onAfterEnter, maybe we set
    // style in the next frame, but the transition has been ended.
    // e.g.
    // onEnter(el) => {
    //     el.style.height = 0; 
    //     nextFrame(() => el.style.heigth = '100px');
    // },
    // onAfterEnter(el) {
    //     el.style.height = '';
    // }
    // if (el._enterCb) {
    // el._enterCb();
    // }
}
function applyTransition(vNode) {
    var position = vNode.position, newPosition = vNode.newPosition;
    var dx = position.left - newPosition.left;
    var dy = position.top - newPosition.top;
    if (dx || dy) {
        var s = findDomFromVNode(vNode, true).style;
        s.transform = s.webkitTransform = "translate(".concat(dx, "px, ").concat(dy, "px)");
        s.transitionDuration = s.webkitTransitionDuration = '0s';
        return vNode;
    }
}

// @reference from vue3.0
function provide(key, value) {
    if (process.env.NODE_ENV !== 'production') {
        if (!currentInstance) {
            throwError('provide() can only be used inside init()');
        }
    }
    var provides = currentInstance.$provides;
    if (provides === null) {
        provides = currentInstance.$provides = Object.create(null);
    }
    else {
        var parent_1 = currentInstance.$senior;
        var parentProvides = parent_1 && parent_1.$provides;
        if (provides === parentProvides) {
            provides = currentInstance.$provides = Object.create(parentProvides);
        }
    }
    provides[key] = value;
}
function inject(key, defaultValue) {
    if (process.env.NODE_ENV !== 'production') {
        if (!currentInstance) {
            throwError('inject() can only be used inside init()');
        }
    }
    var parent = currentInstance.$senior;
    var provides = parent && parent.$provides;
    if (provides && key in provides) {
        return provides[key];
    }
    else if (!isUndefined(defaultValue)) {
        return defaultValue;
    }
    else if (process.env.NODE_ENV !== 'production') {
        error("injection \"".concat(String(key), "\" not found."));
    }
}

function makeLifecycle(name) {
    return function (hook) {
        if (process.env.NODE_ENV !== 'production') {
            if (!currentInstance) {
                throwError("on".concat(name[1].toUpperCase() + name.substr(2), "() can only be used inside init()"));
            }
        }
        currentInstance.on(name, hook);
    };
}
var onInited = makeLifecycle('$inited');
var onBeforeMount = makeLifecycle('$beforeMount');
var onMounted = makeLifecycle('$mounted');
var onBeforeUpdate = makeLifecycle('$beforeUpdate');
var onUpdated = makeLifecycle('$updated');
var onBeforeUnmount = makeLifecycle('$beforeUnmount');
var onUnmounted = makeLifecycle('$unmounted');

function compile() {
    if (process.env.NODE_ENV !== 'production') {
        error('Runtime compilation is not support in this build of Intact.');
    }
}
registerCompile(compile);

export { Component, Transition, TransitionGroup, addTransitionClass, inject, nextTick, onBeforeMount, onBeforeUnmount, onBeforeUpdate, onInited, onMounted, onUnmounted, onUpdated, provide, removeTransitionClass, setInstance, useInstance, watch, whenTransitionEnds };
